import { CommonGTMEvents, CommonTrackingParams, EventNameFormat, TrackingEvent } from 'types/tracking'
import type { EventTrackingParameters } from 'types/event-tracking-parameters'
import posthog, { CaptureOptions, Properties } from 'posthog-js'
import { PORTAL_URL } from 'constants/constants'
import { stringify } from 'qs'
import Router from 'next/router'

/**
 * Tracks events by pushing data to GTM
 *
 * @param {CommonGTMEvents | string} event
 * @param {EventTrackingParameters} parameters
 */
export const trackGTMEvent = (event: CommonGTMEvents | string, parameters?: EventTrackingParameters): void => {
  if ((window as any).dataLayer) {
    ;(window as any).dataLayer.push({ event, ...parameters })
  }
}

/**
 * Resets the data layer
 */
export const resetDataLayer = (): void => {
  if ((window as any).dataLayer) {
    ;(window as any).dataLayer.push(() => {
      ;(window as any).dataLayer.reset()
    })
  }
}

/**
 * Tracks an event using PostHog
 * @param {TrackingEvent} event - Object of params to format the event name. (snake_case)
 * @param {Properties} data - Additional properties to send with the event.
 * @param {CaptureOptions} options - Optional capture options for PostHog.
 */
export const trackEvent = (event: TrackingEvent, data?: Properties, options?: CaptureOptions) => {
  const eventName: EventNameFormat = event.object
    ? `${event.category}:${event.object}_${event.action}`
    : `${event.category}:${event.action}`

  // Add current language to the event data to be able to filter all events by language in PostHog if necessary.
  // User behavior between can differ greatly between French & Dutch-speaking users.
  const dataWithLanguage = {
    ...data,
    [CommonTrackingParams.CURRENT_LANGUAGE]: Router.locale
  }

  posthog.capture(eventName, dataWithLanguage, options)
}

/**
 * Tags every following PostHog event (including autocapture & heatmap clicks) with the active flow step (Session-scoped).
 * @param {string} step
 */
export const registerTrackingStep = (step: string) => {
  posthog.register_for_session({ [CommonTrackingParams.STEP]: step })
}

/**
 * Stops tagging PostHog events with a flow step.
 */
export const unregisterTrackingStep = () => {
  posthog.unregister_for_session(CommonTrackingParams.STEP)
}

/**
 * Adds properties to the current PostHog person without changing its identity.
 * @param {Record<string, unknown>} data
 */
export const setPostHogPersonProperties = (data: Record<string, unknown>) => {
  const properties = { ...data }

  // Ensure email is lowercase if it exists
  if (properties.email) {
    properties.email = (properties.email as string).toLowerCase()
  }

  posthog.setPersonProperties(properties)
}

/**
 * Clears the identified person when a flow is abandoned so the next visitor starts anonymously.
 */
export const resetPostHogUser = () => {
  posthog.reset()
}

/**
 * Identifies a prospect using its stable Bolt opportunity ID.
 * @param {string} opportunityId
 * @param {Record<string, unknown>} data
 */
export const identifyUser = (opportunityId: string, data: Record<string, unknown> = {}) => {
  if (!opportunityId) return

  const properties = { ...data }

  // Ensure email is lowercase if it exists
  if (properties.email) {
    properties.email = (properties.email as string).toLowerCase()
  }

  posthog.identify(opportunityId, properties)
}

/**
 * Generates a URL to the portal with PostHog tracking parameters appended as query strings.
 * @param {string} [path]
 * @returns {string}
 */
export const getPortalUrl = (path?: string): `${typeof PORTAL_URL}${string}` => {
  const posthogSessionId = posthog.get_session_id()
  const posthogDistinctId = posthog.get_distinct_id()
  const posthogUrlQuery = stringify(
    { ph_distinct_id: posthogDistinctId || null, ph_session_id: posthogSessionId || null },
    { skipNulls: true, addQueryPrefix: true }
  )

  const pathWithSlash = path?.startsWith('/') ? path : `/${path}`

  return `${PORTAL_URL}${path ? pathWithSlash : ''}${posthogUrlQuery}`
}
